import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { permissionService } from "../services/permissionService";
import { permissionDisplayId } from "./PermissionCommonStore";
import { Permission } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class PermissionViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = permissionService;
  permissionInstance: Permission | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  name_esValue: string = "";
  name_enValue: string = "";
  name_ptValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadPermissionById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.permissionOne({ id });
    this.permissionInstance = result.permissionOne;
    if (this.permissionInstance) {
      this.loadValues(this.permissionInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.permissionInstance) {
      return permissionDisplayId(this.permissionInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PermissionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.permissionInstance = null;
    this.name_esValue = "";
    this.name_enValue = "";
    this.name_ptValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelPermission() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("permission_update");
  }

  editPermission() {
    const id = this.permissionInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/permission/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printPermission(handlePrint: () => void): void {
    handlePrint();
  }

  *closePermission(): any {
    yield this.close();
  }

  loadValues(instance: Permission) {
    this.idValue = instance.id;
    this.name_esValue = instance.name_es;
    this.name_enValue = instance.name_en;
    this.name_ptValue = instance.name_pt;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadPermissionById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
